// src/pages/support/HelpCenter.tsx

import React, {
  useState,
} from "react";

import {
  Link,
} from "react-router-dom";

import "./SupportPages.css";


// =====================================================
// HELP ITEMS
// =====================================================

const HELP_ITEMS = [
  {
    category: "Getting Started",
    title: "How do I start playing?",
    content:
      "Sign in to your P-ARENA account, open the Game House, choose an available game, and select the supported play mode. Depending on the game, you may be able to play against AI, friends, or online players.",
  },
  {
    category: "Account",
    title: "Do I need an account to play?",
    content:
      "P-ARENA uses registered accounts for player profiles, game statistics, multiplayer features, and other account-based functionality.",
  },
  {
    category: "Games",
    title: "Where can I learn the rules?",
    content:
      "Open Game Guides from the P-ARENA footer. Fasoor has its own guide, and guides for additional games can be provided as those games become available.",
  },
  {
    category: "Fasoor",
    title: "How do I learn Fasoor?",
    content:
      "Visit the Fasoor Game Guide for gameplay rules, card behavior, scoring, Soor rules, and other information about playing Fasoor on P-ARENA.",
  },
  {
    category: "Multiplayer",
    title: "Can I play with another person?",
    content:
      "P-ARENA is designed to support multiplayer gameplay. Available multiplayer options depend on the game and the features currently enabled on the platform.",
  },
  {
    category: "AI",
    title: "Can I play against AI?",
    content:
      "Yes. Where AI mode is available, P-ARENA can create a game with a computer-controlled opponent.",
  },
  {
    category: "Profile",
    title: "Where are my wins and losses stored?",
    content:
      "For supported completed games, P-ARENA can associate game statistics such as games played, wins, losses, scores, and other statistics with your registered account.",
  },
  {
    category: "Technical",
    title: "What should I do if a game stops working?",
    content:
      "First refresh the page and confirm that your internet connection is working. If the problem continues, use Report a Problem and include what happened, what you expected to happen, and the steps that caused the issue.",
  },
];


// =====================================================
// HELP CENTER
// =====================================================

const HelpCenter: React.FC = () => {

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    openItem,
    setOpenItem,
  ] = useState<number | null>(
    null
  );


  // ===================================================
  // FILTER
  // ===================================================

  const normalizedSearch =
    search
      .trim()
      .toLowerCase();

  const filteredItems =
    HELP_ITEMS.filter(
      (item) => {

        if (!normalizedSearch) {
          return true;
        }

        return (
          item.title
            .toLowerCase()
            .includes(
              normalizedSearch
            ) ||
          item.content
            .toLowerCase()
            .includes(
              normalizedSearch
            ) ||
          item.category
            .toLowerCase()
            .includes(
              normalizedSearch
            )
        );
      }
    );


  // ===================================================
  // UI
  // ===================================================

  return (
    <main className="support-page">

      {/* ============================================= */}
      {/* HERO                                          */}
      {/* ============================================= */}

      <section className="support-hero">

        <div className="support-hero-glow" />

        <div className="support-hero-content">

          <nav
            className="support-breadcrumb"
            aria-label="Breadcrumb"
          >
            <Link to="/">
              P-ARENA
            </Link>

            <span>/</span>

            <span>
              Support
            </span>

            <span>/</span>

            <strong>
              Help Center
            </strong>
          </nav>

          <div className="support-emblem">
            ?
          </div>

          <p className="support-eyebrow">
            P-ARENA SUPPORT
          </p>

          <h1>
            How Can We Help?
          </h1>

          <p className="support-hero-description">
            Find answers about your account,
            games, multiplayer, AI opponents,
            profiles, and common technical
            problems.
          </p>


          {/* ========================================= */}
          {/* SEARCH                                    */}
          {/* ========================================= */}

          <div className="support-search">

            <span
              className="support-search-icon"
              aria-hidden="true"
            >
              ⌕
            </span>

            <input
              type="search"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Search the Help Center..."
              aria-label="Search Help Center"
            />

            {search && (
              <button
                type="button"
                onClick={() =>
                  setSearch("")
                }
                aria-label="Clear search"
              >
                ×
              </button>
            )}

          </div>

        </div>

      </section>


      {/* ============================================= */}
      {/* CONTENT                                       */}
      {/* ============================================= */}

      <div className="support-content">

        {/* =========================================== */}
        {/* QUICK ACTIONS                               */}
        {/* =========================================== */}

        <section className="support-section">

          <div className="support-section-heading">

            <span>
              01
            </span>

            <div>

              <p>
                QUICK ACCESS
              </p>

              <h2>
                What Do You Need?
              </h2>

            </div>

          </div>


          <div className="support-action-grid">

            <Link
              to="/guides/fasoor"
              className="support-action-card"
            >

              <span className="support-card-symbol">
                ♣
              </span>

              <small>
                GAMEPLAY
              </small>

              <h3>
                Fasoor Guide
              </h3>

              <p>
                Learn how Fasoor works,
                including gameplay and scoring.
              </p>

              <strong>
                View Guide →
              </strong>

            </Link>


            <Link
              to="/profile"
              className="support-action-card"
            >

              <span className="support-card-symbol">
                ♠
              </span>

              <small>
                ACCOUNT
              </small>

              <h3>
                Player Profile
              </h3>

              <p>
                Access your P-ARENA player
                profile and account information.
              </p>

              <strong>
                Open Profile →
              </strong>

            </Link>


            <Link
              to="/contact"
              className="support-action-card"
            >

              <span className="support-card-symbol">
                ♦
              </span>

              <small>
                SUPPORT
              </small>

              <h3>
                Contact Us
              </h3>

              <p>
                Have a question that is not
                answered here? Contact P-ARENA.
              </p>

              <strong>
                Contact Support →
              </strong>

            </Link>


            <Link
              to="/report"
              className="support-action-card"
            >

              <span className="support-card-symbol">
                !
              </span>

              <small>
                TECHNICAL
              </small>

              <h3>
                Report a Problem
              </h3>

              <p>
                Tell us about a bug, game issue,
                or technical problem.
              </p>

              <strong>
                Report Problem →
              </strong>

            </Link>

          </div>

        </section>


        {/* =========================================== */}
        {/* FAQ                                         */}
        {/* =========================================== */}

        <section className="support-section">

          <div className="support-section-heading">

            <span>
              02
            </span>

            <div>

              <p>
                COMMON QUESTIONS
              </p>

              <h2>
                Frequently Asked Questions
              </h2>

            </div>

          </div>


          <div className="support-faq-list">

            {filteredItems.length > 0 ? (

              filteredItems.map(
                (item) => {

                  const originalIndex =
                    HELP_ITEMS.indexOf(
                      item
                    );

                  const isOpen =
                    openItem ===
                    originalIndex;

                  return (
                    <article
                      key={item.title}
                      className={`
                        support-faq-item
                        ${
                          isOpen
                            ? "open"
                            : ""
                        }
                      `}
                    >

                      <button
                        type="button"
                        onClick={() =>
                          setOpenItem(
                            isOpen
                              ? null
                              : originalIndex
                          )
                        }
                        aria-expanded={
                          isOpen
                        }
                      >

                        <span className="support-faq-category">
                          {item.category}
                        </span>

                        <span className="support-faq-question">
                          {item.title}
                        </span>

                        <span
                          className="support-faq-toggle"
                          aria-hidden="true"
                        >
                          {isOpen
                            ? "−"
                            : "+"}
                        </span>

                      </button>


                      {isOpen && (
                        <div className="support-faq-answer">

                          <p>
                            {item.content}
                          </p>

                        </div>
                      )}

                    </article>
                  );
                }
              )

            ) : (

              <div className="support-empty">

                <span>
                  ♣
                </span>

                <h3>
                  No answers found
                </h3>

                <p>
                  Try another search or
                  contact P-ARENA support.
                </p>

                <Link to="/contact">
                  Contact Us
                </Link>

              </div>

            )}

          </div>

        </section>


        {/* =========================================== */}
        {/* SUPPORT CTA                                 */}
        {/* =========================================== */}

        <section className="support-cta">

          <div className="support-cta-suits">
            ♠ ♥ ♦ ♣
          </div>

          <small>
            STILL NEED HELP?
          </small>

          <h2>
            We're Here to Help
          </h2>

          <p>
            If you could not find the answer
            you need, contact us or report a
            technical problem.
          </p>

          <div className="support-cta-actions">

            <Link
              to="/contact"
              className="support-primary-button"
            >
              Contact Us
            </Link>

            <Link
              to="/report"
              className="support-secondary-button"
            >
              Report a Problem
            </Link>

          </div>

        </section>

      </div>

    </main>
  );
};


export default HelpCenter;